/**
 * InlineAWSConnect — Phase 2 Tactical Win #1.
 *
 * 30-second AWS connect modal embedded in the OnboardingChecklist on
 * /dashboard. Walks the user through 3 steps without leaving the
 * dashboard:
 *
 *   1. Copy the read-only IAM policy JSON
 *   2. Paste the resulting IAM Role ARN + External ID
 *   3. Test the connection — green check = ready
 *
 * Calls the existing /api/aws-probe/* endpoints (Engine #28). On
 * success, fires the parent `onConnected()` callback so the
 * checklist's AWS-connect step flips to ✅.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Cloud, Copy, CheckCircle2, AlertTriangle, ArrowRight,
  Loader2, ExternalLink, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function InlineAWSConnect({ open, onClose, onConnected }) {
  const [step, setStep] = useState(1);
  const [policy, setPolicy] = useState('');
  const [externalId, setExternalId] = useState('');
  const [cartieAccountId, setCartieAccountId] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);  // null | 'ok' | 'fail'

  // Load the read-only policy + a fresh external_id when modal opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/idle-killer/cloud-connect/policies`, auth());
        setPolicy(r.data?.readonly_policy_json || '');
        setExternalId(r.data?.external_id || '');
        setCartieAccountId(r.data?.cartie_master_account_id || '');
      } catch (e) {
        toast.error('Could not load IAM policy bundle');
      }
    })();
    setStep(1);
    setRoleArn('');
    setResult(null);
  }, [open]);

  const copyPolicy = async () => {
    try {
      await navigator.clipboard.writeText(policy);
      toast.success('Policy JSON copied — paste it as a new IAM policy in AWS');
    } catch {
      toast.message('Couldn\'t copy. Select the text manually.');
    }
  };

  const testConnection = async () => {
    if (!roleArn.startsWith('arn:aws:iam::')) {
      toast.error('Role ARN should look like arn:aws:iam::123456789012:role/CartieReadOnly');
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      // 1. Register the role ARN
      await axios.post(`${API}/idle-killer/cloud-connect/readonly`,
        { role_arn: roleArn }, auth());
      // 2. Verify by assuming the role
      const v = await axios.post(`${API}/idle-killer/cloud-connect/verify`, {}, auth());
      if (v.data?.ok || v.data?.account_id) {
        setResult('ok');
        toast.success('AWS connected — Bill Detective will populate within 60s');
        onConnected?.(v.data);
      } else {
        setResult('fail');
        toast.error(v.data?.reason || 'Could not assume role. Check policy + external ID.');
      }
    } catch (e) {
      setResult('fail');
      toast.error(e.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 ring-1 ring-slate-800 text-slate-100 max-w-2xl"
                     data-testid="inline-aws-connect-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-emerald-300" />
            Connect AWS in 30 seconds
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Read-only by default. No destructive permissions. Revoke any time.
          </DialogDescription>
        </DialogHeader>

        {/* Step pills */}
        <div className="flex items-center gap-2 text-xs">
          {[1, 2, 3].map((n) => (
            <div key={n}
                 className={`flex items-center gap-1.5 px-3 py-1 rounded-full ring-1 ${
                   step >= n
                     ? 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-300'
                     : 'bg-slate-800/40 ring-slate-800 text-slate-500'
                 }`}>
              <span className="w-4 h-4 rounded-full bg-slate-950 flex items-center justify-center font-mono text-[10px]">{n}</span>
              {n === 1 && 'Copy policy'}
              {n === 2 && 'Create role'}
              {n === 3 && 'Test'}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div data-testid="aws-connect-step-1">
            <p className="text-sm text-slate-300 mb-2">
              In your AWS console → IAM → Policies → &ldquo;Create policy&rdquo; → paste the JSON below:
            </p>
            <div className="relative">
              <pre className="max-h-48 overflow-auto p-3 rounded-md bg-slate-950 ring-1 ring-slate-800 text-[11px] text-emerald-200 font-mono whitespace-pre-wrap" data-testid="aws-connect-policy-json">
                {policy || 'Loading IAM policy…'}
              </pre>
              <Button
                onClick={copyPolicy}
                size="sm"
                className="absolute top-2 right-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                data-testid="aws-connect-copy-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div className="mt-3 text-xs text-slate-400">
              Name it <code className="text-emerald-300 bg-slate-800 px-1 rounded">CartieReadOnly</code> — we&apos;ll reference it in step 2.
            </div>
            <Button onClick={() => setStep(2)}
                    className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                    data-testid="aws-connect-next-1">
              I copied the policy <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-3" data-testid="aws-connect-step-2">
            <p className="text-sm text-slate-300">
              Now create an IAM Role: <strong>Roles → Create role → Another AWS account</strong>.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-md bg-slate-950 ring-1 ring-slate-800">
                <div className="text-slate-500 mb-1">Trusted account ID</div>
                <div className="font-mono text-emerald-300 select-all" data-testid="aws-connect-trusted-account">
                  {cartieAccountId || '—'}
                </div>
              </div>
              <div className="p-3 rounded-md bg-slate-950 ring-1 ring-slate-800">
                <div className="text-slate-500 mb-1">External ID (paste verbatim)</div>
                <div className="font-mono text-emerald-300 select-all break-all" data-testid="aws-connect-external-id">
                  {externalId || '—'}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Attach the <code className="text-emerald-300 bg-slate-800 px-1 rounded">CartieReadOnly</code> policy you just created → review → create.
            </p>
            <p className="text-xs text-slate-400">
              Open the role you just created → copy its <strong>ARN</strong> (looks like <code className="text-emerald-300 bg-slate-800 px-1 rounded">arn:aws:iam::&lt;your-account&gt;:role/CartieReadOnly</code>) → next step.
            </p>
            <div className="flex justify-between gap-2 mt-2">
              <Button variant="outline" onClick={() => setStep(1)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800">Back</Button>
              <Button onClick={() => setStep(3)}
                      className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                      data-testid="aws-connect-next-2">
                Role created <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="space-y-3" data-testid="aws-connect-step-3">
            <Label className="text-xs text-slate-400">Paste the Role ARN</Label>
            <Input
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              placeholder="arn:aws:iam::123456789012:role/CartieReadOnly"
              className="bg-slate-950 border-slate-700 text-slate-100 font-mono text-xs"
              data-testid="aws-connect-role-arn-input"
            />

            <Button onClick={testConnection}
                    disabled={testing || !roleArn}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                    data-testid="aws-connect-test-btn">
              {testing
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Probing your account…</>)
                : (<><ShieldCheck className="w-4 h-4 mr-2" /> Test &amp; connect</>)}
            </Button>

            {result === 'ok' && (
              <div className="p-3 rounded-md bg-emerald-500/10 ring-1 ring-emerald-500/30 text-sm text-emerald-300 flex items-start gap-2"
                   data-testid="aws-connect-result-ok">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Connection verified. Bill Detective leaderboard will populate within ~60 seconds as we scan your bill.</span>
              </div>
            )}
            {result === 'fail' && (
              <div className="p-3 rounded-md bg-rose-500/10 ring-1 ring-rose-500/30 text-sm text-rose-300 flex items-start gap-2"
                   data-testid="aws-connect-result-fail">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Couldn&apos;t assume the role. Double-check the policy is attached and the external-ID matches verbatim.</span>
              </div>
            )}

            <div className="flex justify-between gap-2 mt-2">
              <Button variant="outline" onClick={() => setStep(2)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800">Back</Button>
              <a href="https://console.aws.amazon.com/iam/home#/roles" target="_blank" rel="noopener noreferrer"
                 className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 self-center"
                 data-testid="aws-connect-iam-link">
                Open AWS IAM console <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="aws-connect-cancel">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
